import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { getModelData } from '../graphql/queries'
import { updateModelAdmin } from '../graphql/mutation'
import { DataGrid } from '@mui/x-data-grid'

import ProgressBar from '../components/circularProgress/circularProgress'
import ModelIcon from '@mui/icons-material/Category'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import DownloadIcon from '@mui/icons-material/Download'
import ImageIcon from '@mui/icons-material/Image'
import {
  Typography,
  Box,
  Dialog,
  Card,
  CardContent,
  Chip,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Alert,
  Snackbar,
  Grid,
  Divider,
  Paper
} from '@mui/material'

export default function SingleModelPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  
  const { loading, data, refetch } = useQuery(getModelData, {
    variables: { modelId: id },
  })

  const [updateModel, { loading: updating }] = useMutation(updateModelAdmin, {
    onCompleted: () => {
      setSnackbar({ open: true, message: 'Status updated successfully!', severity: 'success' })
      refetch()
    },
    onError: (error) => {
      setSnackbar({ open: true, message: `Error: ${error.message}`, severity: 'error' })
    }
  })

  const [editingStatus, setEditingStatus] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState('')
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

  const statusOptions = ['starting', 'processing', 'succeeded', 'canceled', 'failed']
  
  const getStatusColor = (status) => {
    const colors = {
      starting: '#2196f3',
      processing: '#ff9800',
      succeeded: '#4caf50',
      canceled: '#9e9e9e',
      failed: '#f44336'
    }
    return colors[status] || '#9e9e9e'
  }

  const handleStatusUpdate = async () => {
    try {
      await updateModel({
        variables: {
          modelId: id,
          status: selectedStatus
        }
      })
      setEditingStatus(false)
    } catch (error) {
      // Error handled by onError callback
    }
  }

  const startEditingStatus = (currentStatus) => {
    setSelectedStatus(currentStatus)
    setEditingStatus(true)
  }

const setColumns = [
  {
    field: 'name',
    headerName: 'Name',
    width: 400,
  },
  {
    field: 'createdAt',
    headerName: 'Created At',
    width: 200,
  },
]
const handleRowClick = (params) => {
    navigate(`/sets/${params.id}`)
  }

  const [openImage, setOpenImage] = useState(false)

  if (loading) return <ProgressBar />

  const setData = data?.getModel ?? null

  if (!setData) return <Box>No Data Available</Box>
  const sets = (data?.getModel?.sets ?? []).map((item, index) => ({
    ...item,
    id: item.id || index,
  }))
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = (seconds % 60).toFixed(2)
    return `${minutes} min ${remainingSeconds} sec`
  }


  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        py: 4,
      }}
    >
      <Box
        sx={{
          maxWidth: '1400px',
          mx: 'auto',
          px: 3,
        }}
      >
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ModelIcon fontSize="large" />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                Model Details
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Manage and monitor your AI model
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Grid container spacing={3}>
          {/* Model Information Card */}
          <Grid item xs={12} md={8}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                overflow: 'visible'
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1e293b' }}>
                  Model Information
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', mb: 1, fontWeight: 500 }}>
                        Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                        {setData.name || '-'}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', mb: 1, fontWeight: 500 }}>
                        User ID
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                        {setData.userId || '-'}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', mb: 1, fontWeight: 500 }}>
                        Status
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {editingStatus ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <Select
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                disabled={updating}
                              >
                                {statusOptions.map((status) => (
                                  <MenuItem key={status} value={status}>
                                    <Chip
                                      label={status}
                                      size="small"
                                      sx={{
                                        backgroundColor: getStatusColor(status),
                                        color: 'white',
                                        fontWeight: 500,
                                        textTransform: 'capitalize'
                                      }}
                                    />
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <IconButton
                              onClick={handleStatusUpdate}
                              disabled={updating}
                              color="success"
                              size="small"
                            >
                              <CheckIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => setEditingStatus(false)}
                              disabled={updating}
                              color="error"
                              size="small"
                            >
                              <CloseIcon />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={setData.status}
                              sx={{
                                backgroundColor: getStatusColor(setData.status),
                                color: 'white',
                                fontWeight: 500,
                                textTransform: 'capitalize'
                              }}
                            />
                            <IconButton
                              onClick={() => startEditingStatus(setData.status)}
                              size="small"
                              sx={{ ml: 1 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', mb: 1, fontWeight: 500 }}>
                        Predict Time
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                        {setData.predict_time ? formatTime(setData.predict_time) : '-'}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', mb: 1, fontWeight: 500 }}>
                        Created At
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                        {new Date(setData.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', mb: 1, fontWeight: 500 }}>
                        Updated At
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                        {new Date(setData.updatedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {setData.instanceData && (
                  <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e2e8f0' }}>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      href={setData.instanceData}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      Download Instance Data
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Model Image Card */}
          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                height: 'fit-content'
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <ImageIcon sx={{ color: '#64748b' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    Model Preview
                  </Typography>
                </Box>
                
                {setData.image ? (
                  <Box
                    component="img"
                    src={setData.image}
                    alt="Model Preview"
                    onClick={() => setOpenImage(true)}
                    sx={{
                      width: '100%',
                      maxHeight: 300,
                      objectFit: 'cover',
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.02)'
                      }
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: 200,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f1f5f9',
                      borderRadius: 2,
                      border: '2px dashed #cbd5e1'
                    }}
                  >
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      No image available
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Sets Table Card */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                mt: 2
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1e293b' }}>
                  Associated Sets ({sets.length})
                </Typography>
                <Box sx={{ height: 400, width: '100%' }}>
                  <DataGrid
                    rows={sets}
                    columns={setColumns}
                    pageSize={5}
                    rowsPerPageOptions={[5, 10, 25]}
                    pagination
                    autoHeight
                    disableColumnMenu
                    onRowClick={handleRowClick}
                    sx={{
                      border: 'none',
                      '& .MuiDataGrid-cell': {
                        borderBottom: '1px solid #f1f5f9',
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#f8fafc',
                        borderBottom: '1px solid #e2e8f0',
                      },
                      '& .MuiDataGrid-row:hover': {
                        backgroundColor: '#f8fafc',
                        cursor: 'pointer'
                      }
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Image Dialog */}
        <Dialog 
          open={openImage} 
          onClose={() => setOpenImage(false)} 
          maxWidth="lg"
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxWidth: '90vw',
              maxHeight: '90vh'
            }
          }}
        >
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <img
              src={setData.image}
              alt="Model Preview - Full Size"
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: 8
              }}
            />
          </Box>
        </Dialog>

        {/* Success/Error Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%', borderRadius: 2 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  )
}
